import React, { useEffect, useState } from 'react';
import { 
  Table, 
  message, 
  Button, 
  Space, 
  Row, 
  Col, 
  Input, 
  Typography,
  Divider,
  Statistic,
  Tag,
  Spin,
  Alert
} from 'antd';
import { 
  FileTextOutlined,
  SaveOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { PageTitle, StyledCard, FormLabel } from '../common';
import { theme } from '../../theme';

const { Text } = Typography;

// Interfaces
interface TicketBalanza {
  id: number;
  numero_ticket: number;
  fecha_ticket: string;
  kilos_primera: number;
  kilos_segunda: number;
  kilos_netos: number;
  numero_remito: string;
  dtv: string;
  cliente_id: number;
  compra_servicio: string;
  cierre: string;
}

interface TicketDetallado {
  id: number;
  numero_ticket: number;
  cantidad: number;
  precio: number;
  total: number;
  ticket_balanza: TicketBalanza;
}

interface RetencionInfo {
  id: number;
  descripcion: string;
  tipo: string;
  porcentaje_base: number;
  portonelada_base: number;
  minimo_porton: number;
  minimo_pesos: number;
  acumulado_ganancia: number;
  aplicar: string;
  provincia: string | null;
}

interface RetencionDetallada {
  id: number;
  portonelada: number;
  porcentaje: number;
  importe: number;
  total: number;
  retencion_info: RetencionInfo;
}

interface LiquidacionData {
  id: number;
  nro_compra: number;
  comprobante: string;
  fecha: string;
  cliente: number;
  cliente_nombre: string;
  cliente_nc: string;
  subtotal: number;
  iva: number;
  impuesto_interno: number;
  retenciones: number;
  alta: number;
  empresa: number;
  empresa_nombre: string;
  sucursal: number;
  sucursal_nombre: string;
}

interface LiquidacionResponse {
  liquidacion: LiquidacionData;
  tickets: TicketDetallado[];
  retenciones: RetencionDetallada[];
  totales: {
    total_kilos: number;
    total_subtotal: number;
    total_iva: number;
    total_retenciones: number;
    total_general: number;
    cantidad_tickets: number;
    cantidad_retenciones: number;
  };
}

const AsignarComprobante: React.FC = () => {
  const [liquidacionData, setLiquidacionData] = useState<LiquidacionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nuevoComprobante, setNuevoComprobante] = useState('');
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  const { id_liquidacion } = useParams<{ id_liquidacion: string }>();

  // Cargar datos de la liquidación al montar el componente
  useEffect(() => {
    if (user?.id_empresa && user?.id_sucursal && id_liquidacion) {
      fetchLiquidacionData();
    }
  }, [user, id_liquidacion]);

  const fetchLiquidacionData = async () => {
    if (!user?.id_empresa || !user?.id_sucursal || !id_liquidacion) {
      message.error('Faltan datos del usuario o ID de liquidación');
      return;
    }

    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get<LiquidacionResponse>(
        `${process.env.REACT_APP_LIQUIDACION_URL}/obtener_liquidacion_compraalgodon/${user.id_empresa}/${user.id_sucursal}/${id_liquidacion}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      setLiquidacionData(response.data);
      setNuevoComprobante(response.data.liquidacion.comprobante || '');
    } catch (error: any) {
      console.error('Error al cargar liquidación:', error);
      message.error('Error al cargar los datos de la liquidación');
    } finally {
      setLoading(false);
    }
  };

  const handleGuardarComprobante = async () => {
    if (!nuevoComprobante.trim()) {
      message.warning('Por favor ingrese un número de comprobante');
      return;
    }

    if (!liquidacionData || !user?.id_empresa || !user?.id_sucursal || !id_liquidacion) {
      message.error('No hay datos suficientes para actualizar el comprobante');
      return;
    }

    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      
      const response = await axios.put(
        `${process.env.REACT_APP_LIQUIDACION_URL}/asignar_comprobante/${user.id_empresa}/${user.id_sucursal}/${id_liquidacion}/`,
        {
          comprobante: nuevoComprobante
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (response.data.success) {
        message.success(response.data.message);
        
        // Actualizar el estado local
        setLiquidacionData(prev => prev ? {
          ...prev,
          liquidacion: {
            ...prev.liquidacion,
            comprobante: nuevoComprobante
          }
        } : null);
        
        // Navegar de vuelta al listado después de un breve delay
        setTimeout(() => {
          navigate('/listar_compraalgodon');
        }, 1500);
      } else {
        message.error('Error al guardar el comprobante');
      }
      
    } catch (error: any) {
      console.error('Error al guardar comprobante:', error);
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('Error al guardar el comprobante');
      }
    } finally {
      setSaving(false);
    }
  };


  const formatCurrency = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
      return '$ 0,00';
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2,
    }).format(numValue);
  };

  const formatNumber = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
      return '0';
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('es-AR').format(numValue);
  };

  // Columnas para la tabla de tickets
  const columnsTickets: ColumnsType<TicketDetallado> = [
    {
      title: 'N° Ticket',
      dataIndex: 'numero_ticket',
      key: 'numero_ticket',
      width: 100,
    },
    {
      title: 'Fecha',
      dataIndex: ['ticket_balanza', 'fecha_ticket'],
      key: 'fecha_ticket',
      width: 110,
      render: (value) => value ? dayjs(value).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Kilos Netos',
      dataIndex: 'cantidad',
      key: 'cantidad',
      width: 120,
      align: 'right',
      render: (value) => formatNumber(value),
    },
    {
      title: 'Precio/Ton',
      dataIndex: 'precio',
      key: 'precio',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Remito',
      dataIndex: ['ticket_balanza', 'numero_remito'],
      key: 'numero_remito',
      width: 100,
    },
    {
      title: 'DTV',
      dataIndex: ['ticket_balanza', 'dtv'],
      key: 'dtv',
      width: 100,
    },
  ];

  // Columnas para la tabla de retenciones
  const columnsRetenciones: ColumnsType<RetencionDetallada> = [
    {
      title: 'Descripción',
      dataIndex: ['retencion_info', 'descripcion'],
      key: 'descripcion',
      width: 200,
    },
    {
      title: 'Tipo',
      dataIndex: ['retencion_info', 'tipo'],
      key: 'tipo',
      width: 100,
      render: (tipo) => <Tag color="blue">{tipo}</Tag>,
    },
    {
      title: 'Porcentaje',
      dataIndex: 'porcentaje',
      key: 'porcentaje',
      width: 100,
      align: 'right',
      render: (value) => `${value}%`,
    },
    {
      title: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
  ];

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Cargando datos de la liquidación...</div>
      </div>
    );
  }

  if (!liquidacionData) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="Error"
          description="No se pudieron cargar los datos de la liquidación"
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '0' }}>
      <PageTitle style={{ marginBottom: '12px', fontSize: '24px' }}>Asignar Comprobante - Liquidación</PageTitle>

      <StyledCard title="Información de la Liquidación" bodyStyle={{ padding: '12px' }}>
        {/* Información de la liquidación */}
        <StyledCard 
          title="Datos Generales"
          style={{ marginBottom: '12px' }}
          bodyStyle={{ padding: '12px' }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Fecha"
                value={dayjs(liquidacionData.liquidacion.fecha).format('DD/MM/YYYY')}
                prefix={<FileTextOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Empresa"
                value={liquidacionData.liquidacion.empresa_nombre}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Sucursal"
                value={liquidacionData.liquidacion.sucursal_nombre}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Cliente"
                value={`${liquidacionData.liquidacion.cliente_nc} - ${liquidacionData.liquidacion.cliente_nombre}`}
                prefix={<InfoCircleOutlined />}
              />
            </Col>
          </Row>
        </StyledCard>

        {/* Asignación de comprobante */}
        <StyledCard 
          title="Asignar Comprobante"
          style={{ marginBottom: '12px' }}
          bodyStyle={{ padding: '12px' }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', alignItems: 'end' }}>
            <div>
              <FormLabel required>Número de Comprobante</FormLabel>
              <Input
                placeholder="Ingrese el número de comprobante"
                value={nuevoComprobante}
                onChange={(e) => setNuevoComprobante(e.target.value)}
                maxLength={13}
                style={{ 
                  width: '100%',
                  fontSize: '13px',
                  height: '32px'
                }}
              />
            </div>
            <div>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleGuardarComprobante}
                loading={saving}
                size="small"
                style={{
                  backgroundColor: theme.colors.primary,
                  borderColor: theme.colors.primary,
                  fontSize: '12px',
                  height: '28px',
                  width: '100%'
                }}
              >
                Guardar Comprobante
              </Button>
            </div>
            <div>
              {liquidacionData.liquidacion.comprobante && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  padding: '6px 10px',
                  backgroundColor: '#f6ffed',
                  border: `1px solid #b7eb8f`,
                  borderRadius: theme.borderRadius.md,
                  fontSize: '12px'
                }}>
                  <CheckCircleOutlined style={{ color: theme.colors.success, fontSize: '14px' }} />
                  <Text strong style={{ color: theme.colors.success, fontSize: '12px' }}>
                    Comprobante actual: {liquidacionData.liquidacion.comprobante}
                  </Text>
                </div>
              )}
            </div>
          </div>
        </StyledCard>

        {/* Resumen de totales */}
        <StyledCard 
          title="Resumen de Totales"
          style={{ marginBottom: '12px' }}
          bodyStyle={{ padding: '12px' }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={6}>
              <Statistic
                title="Total Kilos"
                value={liquidacionData.totales.total_kilos}
                formatter={(value) => formatNumber(value)}
                suffix="kg"
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title="Subtotal"
                value={liquidacionData.totales.total_subtotal}
                formatter={(value) => formatCurrency(value)}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title="IVA"
                value={liquidacionData.totales.total_iva}
                formatter={(value) => formatCurrency(value)}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title="Total General"
                value={liquidacionData.totales.total_general}
                formatter={(value) => formatCurrency(value)}
                valueStyle={{ color: '#1890ff', fontWeight: 'bold' }}
              />
            </Col>
          </Row>
        </StyledCard>

        {/* Tabla de tickets */}
        <StyledCard 
          title="Tickets"
          style={{ marginBottom: '12px' }}
          bodyStyle={{ padding: '12px' }}
        >
          <Table
            columns={columnsTickets}
            dataSource={liquidacionData.tickets}
            rowKey="id"
            pagination={false}
            scroll={{ x: 800 }}
            size="small"
            bordered
            style={{ fontSize: '12px' }}
            locale={{
              emptyText: 'No hay tickets registrados'
            }}
          />
        </StyledCard>

        {/* Tabla de retenciones */}
        <StyledCard 
          title="Retenciones"
          bodyStyle={{ padding: '12px' }}
        >
          <Table
            columns={columnsRetenciones}
            dataSource={liquidacionData.retenciones}
            rowKey="id"
            pagination={false}
            scroll={{ x: 600 }}
            size="small"
            bordered
            style={{ fontSize: '12px' }}
            locale={{
              emptyText: 'No hay retenciones aplicadas'
            }}
          />
        </StyledCard>
      </StyledCard>
    </div>
  );
};

export default AsignarComprobante;
